import { ExternalSettings } from "src/app/common/services/external-settings/external-settings.interface";
import { Injectable, Renderer2 } from '@angular/core';
import { PlatformService } from "./platform.service";
import { BankTemplate } from "src/app/common/services/bank-template/bank-template-bean";
import { OverlayContainer } from '@angular/cdk/overlay';

@Injectable({
    providedIn: 'root'
})
export class UIUtilityService {

    private static externalSettings: ExternalSettings;
    private static overlayContainer: OverlayContainer;

    public static init(externalSettings: ExternalSettings, overlayContainer: OverlayContainer) {
        this.externalSettings = externalSettings;
        UIUtilityService.overlayContainer = overlayContainer;
    }

    public static applyEnvClass(): void {
      const body = document.body;
      const container = UIUtilityService.overlayContainer?.getContainerElement();
      body.classList.add('theme-'+this.externalSettings.templates.toLowerCase());
      container?.classList.add('theme-'+this.externalSettings.templates.toLowerCase());
      if (PlatformService.isMobile()) {
        body.classList.add('env-app');
        container?.classList.add('env-app');
        body.classList.remove('env-web');
        container?.classList.remove('env-web');
      } else {
        body.classList.add('env-web');
        container?.classList.add('env-web');
        body.classList.remove('env-app');
        container?.classList.remove('env-app');
      }
    }

    public static getBackgroundStyle(): {} {
        if (!this.externalSettings) return {};
        let styleObj: { [key: string]: string } = {
            'background-image': `url(${this.externalSettings.web.siteBackground})`,
            'background-size': 'cover',
            'background-repeat': 'no-repeat',
            'background-position': 'center center',
        };

        if (PlatformService.isMobile()) {
            styleObj['padding-top'] = 'env(safe-area-inset-top)';
            styleObj['padding-bottom'] = 'env(safe-area-inset-bottom)';
        }

        return styleObj;
    }

    public static getMobileViewLoginBackgroundStyle(): {} {
        if (!this.externalSettings) return {};
        return {
            'padding-top': 'env(safe-area-inset-top)',
            'padding-bottom': 'env(safe-area-inset-bottom)',
            'background-image': `url(${this.externalSettings.web.loginBackground})`,
            'background-size': 'cover',
            'background-repeat': 'no-repeat',
            'background-position': 'center center',
        };
    }

    public static getMobileViewMenuBackgroundStyle() { 
        if (!this.externalSettings) return {};
        let styleObj: { [key: string]: string } = {};
        if (PlatformService.isMobile()) {
          styleObj['padding-top'] = 'env(safe-area-inset-top)';
          styleObj['padding-bottom'] = 'env(safe-area-inset-bottom)';
        }
        if (this.hasMobileViewMenuBackground()) {
          styleObj['background-image'] = `url(${this.externalSettings.web.menuBackground})`;
          styleObj['background-size'] = 'cover';
          styleObj['background-repeat'] = 'no-repeat';
          styleObj['background-position'] = 'center center';
        };
        return styleObj;
    }

    public static getMobilePreLoadPageBackgroundStyle() {
        return {
            'z-index': 0,
            'padding-top': 'env(safe-area-inset-top)',
            'padding-bottom': 'env(safe-area-inset-bottom)',
            "background-image": "url('assets/images/customization/bg_login.jpg')",
            "background-size": "cover",
            "background-repeat": "no-repeat",
            "background-position": "center center",
            "height": "100vh",
            "width": "100vh",
            "position": "fixed",
            "overflow": "hidden"
        };
    }

    public static getHeaderStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'color': this.externalSettings.web.color_textheader };
    }

    public static getBankTextColor(): {} {
        if (!this.externalSettings) return {};
        return { 'color': this.externalSettings.web.color_textheader };
    }

    public static getDangerTextColor(): {} {
        if (!this.externalSettings) return '';
        return { 'color': this.externalSettings.web.color_danger_buttonbackground };
    }

    public static getBankButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_buttonbackground, 'border-color': this.externalSettings.web.color_buttonbackground, 'color': 'white' };
    }

    // Colour is defined by initizateColourScheme() in external-settings.service.ts
    public static getPrimaryButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_primary_buttonbackground, 'border-color': this.externalSettings.web.color_primary_buttonbackground, 'color': 'white' };
    }

    public static getSecondaryButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_secondary_buttonbackground, 'border-color': this.externalSettings.web.color_secondary_buttonbackground, 'color': 'black' };
    }

    public static getTertiaryButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_tertiary_buttonbackground, 'border-color': this.externalSettings.web.color_tertiary_buttonbackground, 'color': 'white' };
    }

    public static getDangerButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_danger_buttonbackground, 'border-color': this.externalSettings.web.color_danger_buttonbackground, 'color': 'white' };
    }

    public static getHightLightButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_hightlight_buttonbackground, 'border-color': this.externalSettings.web.color_hightlight_buttonbackground, 'color': 'white' };
    }

    public static getVoiceCommandButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': this.externalSettings.web.color_voice_command_buttonbackground, 'border-color': this.externalSettings.web.color_voice_command_buttonbackground, 'color': 'white' };
    }

    public static getBankButtonColor(): string {
        if (!this.externalSettings) return '';
        return this.externalSettings.web.color_buttonbackground;
    } 

    public static getDangerIconStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'color': this.externalSettings.web.color_danger_buttonbackground };
    }

    public static getWarningIconStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'color': this.externalSettings.web.color_warning_buttonbackground };
    }

    public static getIconStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'color': this.externalSettings.web.color_icon_buttonbackground };
    }

    public static getBankLogo(): string {
        if (!this.externalSettings) return '';
        let logo = this.externalSettings.web.logo;
        return PlatformService.isMobile() ? logo.image! : logo.image + '?lastmod' + logo.size;
    }

    /**
     * Get the Server side Logo
     */
    public static getWebLogo(): string {
        if (!this.externalSettings) return '';
        let logo = this.externalSettings.web.webLogo;
        return logo.image + '?lastmod' + logo.size;
    }

    public static coalesce(value: boolean, className1: string, className2: string): string {
        return value ? className1 : className2;
    }

    /**
     * This function is to preserve the form element consistency to hide element if not need
     * In case the element is not required by form builder, should use *ngIf directly
     * The element will NOT occupied the space even it is hidden
     */
    public static showIfTrue(value: boolean): string {
        return UIUtilityService.coalesce(value, '', 'd-none');
    }

    /**
     * This function is to preserve the form element consistency to hide element if not need
     * In case the element is not required by form builder, should use *ngIf directly
     * The element will still occupied the space even it is hidden
     */
    public static visibleIfTrue(value: boolean): string {
        return UIUtilityService.coalesce(value, '', 'invisible');
    }

    /**
     * Set Material Tab Styel color
     */
    public static setTabStyle(tab: HTMLElement, renderer: Renderer2, isActive: boolean) {
        let bgcolor = isActive ? this.externalSettings.web.color_buttonbackground : '#F5F5F5';
        renderer.setStyle(tab, 'background-color',bgcolor);
    }

    public static returnIfTrue(value: boolean, className: string): string {
        return UIUtilityService.coalesce(value, className, '');
    }

    public static getBgFrostedWhite(alpha: number = 0.8, blur: number = 7): {} {
      return {
        'background': `rgba(255, 255, 255, ${alpha})`,
        'backdrop-filter': `blur(${blur}px)`,
        '-webkit-backdrop-filter': `blur(${blur}px)`,
      };
    }

    public static getBgFrostedBlack(alpha: number = 0.7, blur: number = 10): {} {
      return {
        'background': `rgba(0, 0, 0, ${alpha})`,
        'backdrop-filter': `blur(${blur}px)`,
        '-webkit-backdrop-filter': `blur(${blur}px)`,
      };
    }

    /**
     * Swap class name of an element based on condition
     */
    public static swap(element: HTMLElement, condition: boolean, swapIn: string, swapOut: string) {
        if (condition) {
            element.classList.add(swapIn);
            element.classList.remove(swapOut);
        }
        else {
            element.classList.remove(swapIn);
            element.classList.add(swapOut);
        }
    }

    // The below functions are used for special customization, which is not expected to be commonly used.
    // Will be merged into customization service in the future, but keep it here for now to avoid potential risk.
    public static is1stNational(): boolean {
        if (!this.externalSettings) return false;
        return this.externalSettings.templates === BankTemplate.FIRST_NATIONAL;
    }
    
    public static useSystemMobileViewLoginBackground(): boolean {
        if (!this.externalSettings) return false;
        return !this.is1stNational();
    }

    public static getCustomizedMobileViewLoginBackgroundStyle(): {} {
        if (!this.externalSettings) return {};
        return { 
          'background-color': '#F6F6F7', 
          'padding-top': 'env(safe-area-inset-top)',
          'padding-bottom': 'env(safe-area-inset-bottom)',
        };
        //     'background-image': `url(${this.externalSettings.web.loginBackground})`,
        //     'background-size': 'cover',
        //     'background-repeat': 'no-repeat',
        //     'background-position': 'center center',
    }

    public static hasMobileViewMenuBackground(): boolean {
        if (!this.externalSettings) return false;
        return !this.is1stNational();
    }

    public static getMobileViewMenuBackgroundClass(): string {
        if (!this.externalSettings) return '';
        return this.is1stNational() ? 'bg-menu-mobile-1stnational' : '';
    }

    public static isMobileWhiteTitle(): boolean {
        if (!this.externalSettings) return true;
        return !this.is1stNational();
    }

    public static getMobileActivityBackground(): string {
        if (!this.externalSettings) return '';
        if (!this.is1stNational()) {
            return 'bg-black-mobile';
        } else {
            return 'bg-light-blue-mobile-1stnational';
        }
    }

    public static useSystemStyle(): boolean {
        if (!this.externalSettings) return true;
        return !this.is1stNational();
    }

    public static getCustomizedButtonStyle(): {} {
        if (!this.externalSettings) return {};
        return { 'background-color': '#009CD6', 'color': 'white' };
    }
}